import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock, Phone, ShieldCheck, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getArticleBySlug, blogArticles } from '../data/blogArticles';
import { getAllPlans } from '../lib/policiesData';
import SEOHead, { ADVISOR_PHONE, ADVISOR_PHONE_DISPLAY, SITE_DOMAIN } from '../components/SEOHead';
import { trackWhatsAppClick, trackPhoneClick } from '../services/analytics';

const BlogPost = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 px-6">
        <SEOHead
          title={isTe ? 'గైడ్ లభించలేదు | VSR Insurance Plus' : 'Guide Not Found | VSR Insurance Plus'}
          description={isTe ? 'మీరు కోరిన బీమా గైడ్ లభించలేదు. మా పూర్తి ఆర్టికల్స్ లైబ్రరీని పరిశీలించండి.' : 'The requested insurance guide could not be found. Explore our complete library of insurance articles.'}
          canonicalPath={`/blog/${slug}`}
          noIndex={true}
        />
        <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-4">
          {isTe ? 'గైడ్ లభించలేదు' : 'Guide Not Found'}
        </h1>
        <p className="text-slate-500 mb-6">
          {isTe ? 'మీరు వెతుకుతున్న ఆర్టికల్ మార్చబడి ఉండవచ్చు లేదా అందుబాటులో లేదు.' : 'The article you are looking for may have been moved or renamed.'}
        </p>
        <Link to="/blog" className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors">
          {isTe ? 'అన్ని బీమా గైడ్‌లను చూడండి' : 'Browse All Insurance Guides'}
        </Link>
      </div>
    );
  }

  const allPlans = getAllPlans();
  const relatedPlan = article.relatedPlanId ? allPlans.find(p => p.id === article.relatedPlanId) : null;
  const recentArticles = blogArticles.filter(a => a.slug !== slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': article.title,
    'description': article.description,
    'datePublished': article.publishedDate,
    'dateModified': article.lastReviewed || article.publishedDate,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${SITE_DOMAIN}/blog/${article.slug}`
    },
    'author': {
      '@type': 'Person',
      'name': article.author,
      'jobTitle': 'Senior Insurance Advisor',
      'url': `${SITE_DOMAIN}/about`,
      'sameAs': [
        'https://www.linkedin.com/in/veereddy-srinivas-reddy-23914a5a/'
      ]
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'VSR Insurance Plus',
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_DOMAIN}/assets/images/logo.png`
      }
    }
  };

  const articleWaMsg = isTe
    ? `నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను "${article.title}" చదివాను. దీనికి సంబంధించి వ్యక్తిగత వివరాలు కావాలి.`
    : `Hi V. S. Reddy, I read your article on "${article.title}". Can you provide personalized advice on this topic?`;

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={`${article.seoTitle || article.title} | VSR Insurance Plus`}
        description={article.description}
        canonicalPath={`/blog/${article.slug}`}
        schemaJson={articleSchema}
        breadcrumbs={[
          { name: isTe ? 'బీమా గైడ్‌లు' : 'Insurance Guides', path: '/blog' },
          { name: article.title, path: `/blog/${article.slug}` }
        ]}
      />

      <article className="container mx-auto px-6 max-w-4xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-primary mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> {isTe ? 'అన్ని గైడ్‌లకు తిరిగి వెళ్లండి' : 'Back to Insurance Guides'}
        </Link>

        {/* Article Header */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-4">
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-primary font-bold rounded-full">
              {article.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> {isTe ? 'ప్రచురణ' : 'Published'}: {article.publishedDate}
            </span>
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> {isTe ? 'సమీక్షించబడింది' : 'Reviewed'}: {article.lastReviewed}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> {article.readingTime}
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center flex-shrink-0">
              VSR
            </div>
            <div>
              <div className="font-bold text-slate-900 dark:text-white text-sm">
                {isTe ? 'రచయిత: ' : 'Written by '} {article.author}
              </div>
              <div className="text-xs text-slate-500">
                {isTe ? 'సీనియర్ ఎల్ఐసి సలహాదారు (25+ సంవత్సరాల అనుభవం)' : 'Senior LIC Advisor • 25+ Years Certified Experience'}
              </div>
            </div>
          </div>
        </header>

        {/* Table of Contents */}
        {article.tableOfContents && article.tableOfContents.length > 0 && (
          <aside className="mb-10 p-6 bg-slate-100 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-3">
              {isTe ? 'ఈ గైడ్‌లోని విషయాలు' : 'Table of Contents'}
            </h2>
            <ul className="space-y-1.5 text-sm">
              {article.tableOfContents.map((item, idx) => (
                <li key={item.id} className="text-slate-600 dark:text-slate-400 hover:text-primary">
                  <a href={`#${item.id}`} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{idx + 1}.</span>
                    <span>{item.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Article Body */}
        <div className="prose prose-slate dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 leading-relaxed text-base space-y-6">
          <div dangerouslySetInnerHTML={{
            __html: article.content
              .replace(/### (.*?)\n/g, '<h3 class="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-3">$1</h3>')
              .replace(/## (.*?)\n/g, '<h2 class="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white mt-10 mb-4 pb-2 border-b border-slate-200 dark:border-slate-800">$1</h2>')
              .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-slate-900 dark:text-white">$1</strong>')
              .replace(/\n\n/g, '<p class="mb-4 leading-relaxed">')
              .replace(/- (.*?)\n/g, '<li class="ml-4 list-disc">$1</li>')
          }} />
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2">
            <Tag className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-400 font-semibold">{isTe ? 'టాగ్స్:' : 'Tags:'}</span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full text-xs">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Related Policy CTA */}
        {relatedPlan && (
          <div className="my-12 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-blue-950/40 rounded-3xl border border-blue-200 dark:border-blue-900/50 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-wider block mb-1">
                {isTe ? 'సిఫార్సు చేయబడిన పాలసీ' : 'Featured Policy Review'}
              </span>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                {isTe ? relatedPlan.nameTe || relatedPlan.name : relatedPlan.name}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-lg">
                {isTe ? relatedPlan.shortDescTe || relatedPlan.shortDesc : relatedPlan.shortDesc}
              </p>
            </div>
            <Link
              to={`/plan/${relatedPlan.id}`}
              className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-colors whitespace-nowrap shadow-md"
            >
              {isTe ? 'పాలసీ వివరాలు చూడండి' : 'View Full Policy'}
            </Link>
          </div>
        )}

        {/* Call to Action Box */}
        <div className="my-12 p-8 bg-slate-900 text-white rounded-3xl text-center space-y-4">
          <h2 className="text-2xl font-bold">
            {isTe ? 'ఈ పాలసీపై మరింత సమాచారం కావాలా?' : 'Have Questions About This Policy?'}
          </h2>
          <p className="text-slate-300 text-sm max-w-xl mx-auto">
            {isTe
              ? 'మీ వయస్సు మరియు బడ్జెట్‌కు తగిన ఖచ్చితమైన ప్రీమియం లెక్కలు మరియు ప్రయోజనాల కోసం సీనియర్ సలహాదారు వి. ఎస్. రెడ్డితో మాట్లాడండి.'
              : 'Get personalized premium calculations, benefit tables, and eligibility checks directly from Senior Advisor V. S. Reddy.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            <a
              href={`https://wa.me/919849664800?text=${encodeURIComponent(articleWaMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('blog_article_bottom_wa')}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-bold transition-colors inline-flex items-center justify-center gap-2"
            >
              {isTe ? 'వాట్సాప్‌లో అడగండి' : 'WhatsApp V. S. Reddy'}
            </a>
            <a
              href={`tel:${ADVISOR_PHONE}`}
              onClick={() => trackPhoneClick('blog_article_bottom_phone')}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-bold transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" /> {ADVISOR_PHONE_DISPLAY}
            </a>
          </div>
        </div>

        {/* Recent Guides */}
        {recentArticles.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200 dark:border-slate-800">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
              {isTe ? 'ఇతర ముఖ్యమైన గైడ్‌లు' : 'Related Insurance Guides'}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {recentArticles.map((recent) => (
                <Link
                  key={recent.slug}
                  to={`/blog/${recent.slug}`}
                  className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-primary transition-colors block group"
                >
                  <span className="text-[10px] text-primary font-bold uppercase">{recent.category}</span>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2 mt-1">
                    {recent.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
